/**
 * generatePicksFn - Pure async function for generating picks.
 * Extracted from PicksView to keep that file under the 800-line limit.
 *
 * Contains:
 *   - generatePicks (streaming + POST fallback)
 *   - refreshScoresOnly
 */

import { API_ENDPOINTS, apiUrl } from '@config/api';
import type { GeneratePicksStreamBody } from '@hooks/usePicksStream';
import type { ParlayScore } from '@hooks/useStrategyStats';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import type { League, LeagueKey } from '@parlay-picker/shared/league';
import { leagueToLeagueKey } from '@parlay-picker/shared/league';
import type { LlmProvider } from '@parlay-picker/shared/llmProviders';
import type { QuotaCoinData } from '@parlay-picker/shared/quotaCoin';
import type { ExternalApiHealth } from '@parlay-picker/shared/serviceHealth';
import { sportsbookForLeague } from '@parlay-picker/shared/sportsbook';
import { type PicksData, useGenerationStore } from '@stores/generationStore';
import { getUserFriendlyError, safeParseJson } from '@utils/api';
import type { AnalysisConfig } from './constants';

export interface InlineParlayScore {
  strategyScore: ParlayScore | null;
  predictionScore: ParlayScore | null;
}

interface ErrorData {
  message: string;
  type: string;
  retryAfter?: number;
}

interface PicksResponse {
  error?: string;
  errorType?: string;
  retryAfter?: number;
  picks?: GeneratedParlay[];
  cached?: boolean;
  statsWarning?: string;
  lineMismatchWarning?: string;
  generatedAt?: string;
  generationId?: string;
  propsUnavailable?: boolean;
  parlayScores?: InlineParlayScore[];
  quota?: QuotaCoinData;
}

type ProviderSelection = LlmProvider | 'auto';

export interface GeneratePicksOptions {
  forceRefresh?: boolean;
  selectedGames: string[];
  selectedMarkets: string[];
  bannedPlayers: string[];
  analysisConfig: AnalysisConfig;
  selectedLeague: League;
  user: {
    role?: string;
    preferences?: { preferredSportsbook?: string; preferredEuSportsbook?: string };
  } | null;
  selectedProvider: ProviderSelection;
  isGenerating: boolean;
  modeQuota: { used: number; limit: number; remaining: number } | null;
  // Functions from generationStore destructured at component level
  startGeneration: (mode: AnalysisMode, leagueKey: LeagueKey) => AbortController;
  finishGeneration: () => void;
  saveToHistory: (
    picks: PicksData,
    config: AnalysisConfig,
    gameIds: string[],
    sportsbook: string,
  ) => void;
  setActivePicks: (picks: PicksData) => void;
  setPendingPicks: (picks: PicksData) => void;
  hasActivePicks: () => boolean;
  getAutoApplyPicks: () => boolean;
  decrementQuotaLocally: (mode: AnalysisMode) => void;
  // Callbacks
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  startStream: (body: GeneratePicksStreamBody, options?: { signal?: AbortSignal }) => Promise<void>;
  setError: (err: ErrorData | null) => void;
  setApiHealth: (updater: (prev: ExternalApiHealth) => ExternalApiHealth) => void;
  startHealthPolling: () => void;
  setLastFreshFetch: (date: Date) => void;
  setIsConfigCollapsed: (v: boolean) => void;
  showCoin: (data: QuotaCoinData) => void;
  closeConfigPanel: () => void;
  setShowUpgradeModal: (v: boolean) => void;
  scrollToParlays: () => void;
}

export async function generatePicksFn(options: GeneratePicksOptions): Promise<void> {
  const {
    forceRefresh = false,
    selectedGames,
    selectedMarkets,
    bannedPlayers,
    analysisConfig,
    selectedLeague,
    user,
    selectedProvider,
    isGenerating,
    modeQuota,
    startGeneration,
    finishGeneration,
    saveToHistory,
    setActivePicks,
    setPendingPicks,
    hasActivePicks,
    getAutoApplyPicks,
    decrementQuotaLocally,
    authFetch,
    startStream,
    setError,
    setApiHealth,
    startHealthPolling,
    setLastFreshFetch,
    setIsConfigCollapsed,
    showCoin,
    closeConfigPanel,
    setShowUpgradeModal,
    scrollToParlays,
  } = options;

  if (!selectedGames.length) {
    setError({ message: 'Please select at least one game', type: 'generic' });
    return;
  }

  if (modeQuota && modeQuota.remaining <= 0) {
    setShowUpgradeModal(true);
    return;
  }

  if (isGenerating) return;

  closeConfigPanel();
  setIsConfigCollapsed(true);

  const mode = (analysisConfig.outputOptions?.analysisMode ?? 'standard') as AnalysisMode;
  const controller = startGeneration(mode, leagueToLeagueKey(selectedLeague));
  setError(null);

  const selectedSportsbook = sportsbookForLeague(
    user?.preferences,
    leagueToLeagueKey(selectedLeague),
  );
  const shouldSendBannedPlayers = analysisConfig.banList?.includeBannedPlayers ?? true;
  const bannedPlayersToSend = shouldSendBannedPlayers ? bannedPlayers : [];

  const requestBody = {
    gameIds: selectedGames,
    forceRefresh,
    markets: selectedMarkets,
    bannedPlayers: bannedPlayersToSend,
    analysisMode: mode,
    analysisConfig,
    sportsbook: selectedSportsbook,
    league: selectedLeague,
    ...(user?.role === 'admin' && selectedProvider !== 'auto'
      ? { provider: selectedProvider }
      : {}),
  };

  try {
    let usedStreaming = false;
    // What the store held before this run. finalizeStreaming replaces these on a
    // complete event, so an unchanged reference afterwards means this generation
    // produced nothing and whatever is in the store belongs to an earlier run.
    const picksBeforeRun = useGenerationStore.getState().activePicks;
    try {
      await startStream(requestBody, { signal: controller.signal });
      usedStreaming = true;
    } catch (streamErr) {
      if (streamErr instanceof Error && streamErr.name === 'AbortError') {
        throw streamErr;
      }
      const errStatus = (streamErr as { status?: number }).status;
      if (errStatus) {
        throw streamErr;
      }
      const { currentPhase, streamingParlays: sp } = useGenerationStore.getState();
      // Only block fallback if server actually progressed past the initial phase
      // (startGeneration sets fetching_data immediately, so that alone doesn't mean the server started)
      if ((currentPhase && currentPhase !== 'fetching_data') || sp.length > 0) {
        throw streamErr;
      }
      // Reset streaming phase so the POST fallback shows time-based progress
      useGenerationStore.getState().clearStreaming();
    }

    if (usedStreaming) {
      const storeState = useGenerationStore.getState();
      // Only this run's picks. A stream can close cleanly without a complete event
      // (an idle or proxy timeout on a multi-minute generation), which leaves the
      // previous run's picks in place. Saving those wrote a fresh history entry
      // holding older results, under the older generation's id, so selecting the
      // newest entry replayed the generation before it.
      const streamProducedPicks = storeState.activePicks !== picksBeforeRun;
      const appliedPicks = streamProducedPicks ? storeState.activePicks : null;
      const queuedPicks = storeState.pendingPicks;

      if (queuedPicks && queuedPicks.picks.length > 0) {
        saveToHistory(queuedPicks, analysisConfig, selectedGames, selectedSportsbook);
        if (!queuedPicks.cached) decrementQuotaLocally(mode);
        setLastFreshFetch(new Date());
      } else if (appliedPicks && appliedPicks.picks.length > 0) {
        saveToHistory(appliedPicks, analysisConfig, selectedGames, selectedSportsbook);
        if (!appliedPicks.cached) decrementQuotaLocally(mode);
        setLastFreshFetch(new Date());
        setTimeout(scrollToParlays, 100);
      } else if (!streamProducedPicks) {
        // The stream ended without delivering anything for this run.
        setError({
          message: 'The generation ended before any picks arrived. Please try again.',
          type: 'generic',
        });
      } else {
        const warning = storeState.activePicks?.statsWarning || queuedPicks?.statsWarning;
        setError({
          message: warning || '',
          type: warning ? 'props_unavailable' : 'no_picks',
        });
      }
      return;
    }

    // Fallback: regular POST /api/picks (non-streaming)
    const res = await authFetch(apiUrl(API_ENDPOINTS.PICKS), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    });

    const data = (await safeParseJson(res)) as PicksResponse;
    if (data.error) {
      const errorType = data.errorType || 'generic';
      setError({ message: data.error, type: errorType, retryAfter: data.retryAfter });
      if (errorType === 'api_error' || errorType === 'overloaded_error') {
        setApiHealth((prev) => ({ ...prev, oddsApi: false }));
        startHealthPolling();
      }
      return;
    }

    const parlayScores = data.parlayScores || [];
    const existingPicks = hasActivePicks();
    const hasNewPicks = (data.picks || []).length > 0;
    const autoApply = getAutoApplyPicks();

    const picksData = {
      picks: data.picks || [],
      scores: parlayScores,
      generationId: data.generationId || null,
      cached: data.cached || false,
      statsWarning: data.statsWarning || null,
      lineMismatchWarning: data.lineMismatchWarning || null,
      leagueKey: useGenerationStore.getState().generationLeagueKey,
    };

    const shouldQueue = !autoApply && existingPicks && hasNewPicks;

    if (hasNewPicks) {
      saveToHistory(picksData, analysisConfig, selectedGames, selectedSportsbook);
    }

    if (shouldQueue) {
      setPendingPicks(picksData);
    } else {
      setActivePicks(picksData);
    }

    if (data.generatedAt) {
      setLastFreshFetch(new Date(data.generatedAt));
    }

    if (!data.cached && data.picks && data.picks.length > 0) {
      decrementQuotaLocally(mode);
      if (data.quota) showCoin(data.quota);
    }

    if (!data.picks || data.picks.length === 0) {
      if (data.propsUnavailable) {
        setError({
          message:
            data.statsWarning ||
            'Player props are not yet available for the selected games. Props typically become available closer to game time.',
          type: 'props_unavailable',
        });
      } else if (data.cached) {
        setError({
          message: 'No picks available. Click "Force New" to generate fresh picks.',
          type: 'generic',
        });
      } else {
        setError({ message: data.statsWarning || '', type: 'no_picks' });
      }
    }

    if (!shouldQueue) {
      setTimeout(scrollToParlays, 100);
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      const storeState = useGenerationStore.getState();
      if (storeState.activePicks?.cancelled && storeState.activePicks.picks.length > 0) {
        saveToHistory(storeState.activePicks, analysisConfig, selectedGames, selectedSportsbook);
        decrementQuotaLocally(mode);
      }
      return;
    }
    const errorType = (err as { errorType?: string }).errorType;
    setError({
      message: getUserFriendlyError(err as Error),
      type: errorType || 'generic',
    });
  } finally {
    finishGeneration();
  }
}

// ============================================================

export interface RefreshScoresOptions {
  recs: GeneratedParlay[];
  activePicks: PicksData | null;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  setActivePicks: (picks: PicksData) => void;
}

export async function refreshScoresOnlyFn({
  recs,
  activePicks,
  authFetch,
  setActivePicks,
}: RefreshScoresOptions): Promise<void> {
  if (recs.length === 0) return;

  try {
    const scoringParlays = recs.map((parlay: GeneratedParlay) => ({
      name: (parlay.name || parlay.tier || 'Parlay') as string,
      confidence: parlay.confidence,
      legs: (parlay.legs || []).map((leg) => ({
        playerName: leg.player || leg.team || '',
        propType: leg.statType || leg.propType || '',
        line: leg.line ?? 0,
        direction: leg.overUnder || '',
        // Picks generated before 2026-10-01 carry no per-leg league, so fall back to the parlay's.
        leagueKey: leg.leagueKey ?? parlay.leagueKey,
        team: leg.team,
        opponent: leg.opponentTeam,
        isB2b: leg.isB2b,
        blowoutRisk: leg.blowoutRisk,
      })),
    }));

    const response = await authFetch('/api/scoring/score-parlays', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parlays: scoringParlays }),
    });

    if (response.ok) {
      const data = await response.json();
      const results = data.results || [];
      if (activePicks) {
        setActivePicks({ ...activePicks, scores: results });
      }
    } else {
    }
  } catch (_err) {}
}
